import type {
  PublicBoost,
  VoucherResponseDto,
  QuickPicksPromotionSelection,
} from '~/domains/promotions/types';
import { logError } from '~/common/utils/log';

/**
 * localStorage key owned by the new PromotionsContext. The `splash::`
 * prefix matches the wrapping convention used elsewhere by
 * `useLocalStorageSettings`, but this module reads/writes the key
 * directly so the context is not coupled to that hook.
 */
export const PROMOTIONS_SELECTION_KEY = 'splash::promotions::selection';

/**
 * Legacy key historically used by QuickPicks for both promotion and
 * entry-form state. Read once on first mount when the new key is
 * empty so existing users keep their selection across the cutover.
 */
export const LEGACY_QUICK_PICKS_ENTRY_STATE_KEY = 'splash::quickPicks::entryState';

export type PromotionSelectionStored = {
  selectedBoost: PublicBoost | null;
  selectedVoucher: VoucherResponseDto | null;
};

const EMPTY: PromotionSelectionStored = {
  selectedBoost: null,
  selectedVoucher: null,
};

const isBrowser = (): boolean => typeof window !== 'undefined';

export const readPromotionSelection = (): PromotionSelectionStored => {
  if (!isBrowser()) return EMPTY;

  try {
    const raw = localStorage.getItem(PROMOTIONS_SELECTION_KEY);
    if (!raw || raw === 'undefined') return EMPTY;

    const parsed = JSON.parse(raw) as Partial<PromotionSelectionStored>;
    return {
      selectedBoost: parsed.selectedBoost ?? null,
      selectedVoucher: parsed.selectedVoucher ?? null,
    };
  } catch (error) {
    logError('Failed to read promotion selection from localStorage', error);
    return EMPTY;
  }
};

export const writePromotionSelection = (state: PromotionSelectionStored): void => {
  if (!isBrowser()) return;

  try {
    localStorage.setItem(PROMOTIONS_SELECTION_KEY, JSON.stringify(state));
  } catch (error) {
    logError('Failed to write promotion selection to localStorage', error);
  }
};

/**
 * Read the legacy QuickPicks entry state and pluck the promotion
 * snapshots out of it. Returns `null` if the key is absent, malformed,
 * or contains no usable promotion fields.
 */
export const readLegacyPromotionSnapshots = (): PromotionSelectionStored | null => {
  if (!isBrowser()) return null;

  try {
    const raw = localStorage.getItem(LEGACY_QUICK_PICKS_ENTRY_STATE_KEY);
    if (!raw || raw === 'undefined') return null;

    const parsed = JSON.parse(raw) as QuickPicksPromotionSelection;

    const selectedBoost =
      parsed.selectedBoostId && parsed.selectedBoostSnapshot
        ? (parsed.selectedBoostSnapshot as PublicBoost)
        : null;

    const selectedVoucher =
      parsed.selectedVoucherId && parsed.selectedVoucherSnapshot
        ? (parsed.selectedVoucherSnapshot as VoucherResponseDto)
        : null;

    if (!selectedBoost && !selectedVoucher) return null;

    return { selectedBoost, selectedVoucher };
  } catch (error) {
    logError('Failed to migrate legacy promotion snapshots', error);
    return null;
  }
};
